import { Modal } from "antd";
import useSavePessoaJuridicaUnidade from "features/unidades/useSavePessoaJuridicaUnidade";
import useUnidadePessoaJuridicas from "features/unidades/useUnidadePessoaJuridicas";
import { EmpresaResponse } from "resources/types/empresa";
import { showErrorAxios } from "infra/helpers/alerta";
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import FooterModal from "components/Button/FooterModal";
import { useLoading } from "components/GlobalLoading";
import EmpresaWizardForms from "../../Empresa/FormEmpresa/EmpresaWizardForms";
import { TipoPessoaJuridicaEnum } from "features/unidades/enums";
import { Box, Checkbox, Flex, Text } from "@chakra-ui/react";
import Field from "components/Input/Field";
import SelectBooleano from "components/Input/SelectBooleano";
import SelectPessoas from "components/Input/SelectPessoas";
import { PessoaTipo } from "features/pessoas/typings";
import { TipoEntidadeEnum } from "core/resources/enums/conta";
import FormTipo from "./FormTipo";
import { TipoUnidadePessoaEnum } from "core/features/unidades/typings/unidade";

interface ModalCadastroPessoaJuridicaUnidadeProps {
  isOpen: boolean;
  unidadeId: string;
  id?: string | null;
  onClose: () => any;
  onSaved: any;
  hiddenTipo?: boolean;
}

function ModalCadastroPessoaJuridicaUnidade({
  isOpen,
  unidadeId,
  id,
  onClose,
  onSaved,
  hiddenTipo,
}: ModalCadastroPessoaJuridicaUnidadeProps) {
  const { control, setValue, handleSubmit, watch, formState, reset, trigger } =
    useForm<
      EmpresaResponse & { tipoUnidadePessoaId?: TipoUnidadePessoaEnum | null }
    >({
      reValidateMode: "onChange",
      mode: "onTouched",
    });

  const [currentStep, setCurrentStep] = useState(0);
  const [moradorExistente, setMoradorExistente] = useState(id ? "n" : null);

  useEffect(() => {
    if (moradorExistente === "n") setValue("pessoaId", undefined);
  }, [moradorExistente, setValue]);

  const {
    data,
    isLoading: isLoadingPessoa,
    isFetching,
  } = useUnidadePessoaJuridicas(unidadeId, id);

  const { mutate: handleSave, isLoading } = useSavePessoaJuridicaUnidade(
    unidadeId,
    id
  );

  const handleModalSubmit = (data: EmpresaResponse) => {
    handleSave(
      {
        ...data,
        endereco:
          data?.endereco?.id || data?.endereco?.cep ? data.endereco : undefined,
      },
      {
        onSuccess: () => {
          setTimeout(() => {
            onSaved();
          }, 150);
        },
        onError: (err) => {
          showErrorAxios(err);
        },
      }
    );
  };

  const maxStepIndex = moradorExistente === "s" ? 0 : 1;

  const onNext = async () => {
    const editingValidate = await trigger();

    if (!editingValidate) {
      return;
    }

    if (currentStep === maxStepIndex) {
      handleSubmit(handleModalSubmit)();
    } else {
      setCurrentStep((step) => Math.min(step + 1, maxStepIndex));
    }
  };

  const onPrev = () => {
    if (currentStep === 0) {
      onClose();
      return;
    }

    setCurrentStep((step) => Math.max(step - 1, 0));
  };

  useLoading(isLoading || isFetching || isLoadingPessoa);

  const showTipo =
    (!hiddenTipo && !id) || (!hiddenTipo && id && currentStep === 0);

  return (
    <Modal
      destroyOnClose
      width="1100px"
      footer={
        <FooterModal
          showRegisterAnother={false}
          onSubmit={onNext}
          onClose={onPrev}
          isLoadingSave={isLoading}
          isDisabled={!formState.isValid}
          buttonCancelarText={currentStep === 0 ? "Cancelar" : "Voltar"}
          primaryButtonText={
            currentStep === maxStepIndex ? "Salvar" : "Avançar"
          }
        />
      }
      centered
      bodyStyle={{
        height: "max(80vh, 400px)",
        overflow: "auto",
        paddingBottom: 16,
      }}
      visible={isOpen}
      onCancel={onClose}
    >
      <EmpresaWizardForms
        customHeader={
          <>
            {showTipo ? (
              <FormTipo control={control} trigger={trigger} watch={watch} />
            ) : null}
            {!id ? (
              <Box>
                <Flex my="16px" gap={4} flexDir="row">
                  <Box>
                    <Text fontSize="13px">Empresa já existente?</Text>
                    <SelectBooleano
                      value={moradorExistente}
                      onChange={setMoradorExistente}
                    />
                  </Box>

                  {moradorExistente === "s" ? (
                    <Flex flex={1}>
                      <Field
                        label="Selecionar a pessoa"
                        control={control}
                        name="pessoaId"
                        rules={{ required: "Campo obrigatório" }}
                        render={({ field, isInvalid }) => (
                          <SelectPessoas
                            {...field}
                            isInvalid={isInvalid}
                            tipoPessoa={PessoaTipo.Juridica}
                            tipoEntidades={[TipoEntidadeEnum.Condomino]}
                          />
                        )}
                      />
                    </Flex>
                  ) : null}
                </Flex>

                {moradorExistente ? (
                  <Field
                    control={control}
                    name="pessoa.tipoPessoaJuridicaId"
                    render={({ field }) => (
                      <Checkbox
                        onChange={() => {
                          field.onChange(
                            field.value === TipoPessoaJuridicaEnum.Imobiliaria
                              ? null
                              : TipoPessoaJuridicaEnum.Imobiliaria
                          );
                        }}
                        isChecked={
                          field.value === TipoPessoaJuridicaEnum.Imobiliaria
                        }
                      >
                        É imobiliária?
                      </Checkbox>
                    )}
                  />
                ) : null}
              </Box>
            ) : null}
          </>
        }
        requiredEndereco={false}
        isUnidadePessoaEdit
        hideForm={moradorExistente === "s" || !moradorExistente}
        currentStep={currentStep}
        setCurrentStep={setCurrentStep}
        data={data}
        control={control}
        setValue={setValue}
        watch={watch}
        trigger={trigger}
        reset={reset}
      />
    </Modal>
  );
}

export default ModalCadastroPessoaJuridicaUnidade;
